import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Calendar } from "lucide-react";
import { MAGIC_PHOTOS } from "../data/cruiseData";
import useHomeOrder from "../hooks/useHomeOrder";

// Per-card style + content definitions. Each card has a `featured` (big, left)
// renderer and a `compact` (small, right stack) renderer.
const CARDS = {
 vivid: {
  href: "/cruises/vivid-cruises-sydney",
  testid: "ticketed-vivid",
  ring: "ring-fuchsia-400/30",
  shadowFeatured: "shadow-[0_40px_90px_-30px_rgba(168,85,247,0.45)]",
  shadowCompact: "shadow-[0_30px_70px_-25px_rgba(217,70,239,0.4)]",
  accentText: "text-fuchsia-300",
  accentBadgeBg: "bg-amber-300",
  accentBadgeText: "text-[hsl(280,60%,12%)]",
  img: "https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/iw8alzem_vivid%20cruise%20sydney.jpg",
  alt: "Vivid Sydney cruise · Harbour Bridge lit in blue laser beams reflecting on the water · view from the Magic",
  bgGradient: "bg-gradient-to-t from-[hsl(280,80%,6%)] via-[hsla(280,75%,8%,0.75)] to-[hsla(280,70%,12%,0.25)]",
  bgOverlay1: "bg-[radial-gradient(ellipse_at_30%_85%,hsla(280,75%,6%,0.7)_0%,transparent_55%)]",
  bgOverlay2: "bg-gradient-to-br from-fuchsia-700/20 via-transparent to-amber-400/10 mix-blend-overlay",
  compactBgGradient: "bg-gradient-to-r from-[hsl(280,80%,8%)]/95 via-[hsl(280,70%,12%)]/55 to-transparent",
  compactBgOverlay: "bg-gradient-to-tr from-fuchsia-500/20 via-transparent to-amber-400/15 mix-blend-overlay",
  ribbon: "Most Loved · 2027",
  pricePill: "From $49 · Adult",
  dateLabel: "28 May, 19 June 2027 · 14 nights",
  compactDate: "28 May → 19 Jun",
  compactBadgeText: "From $49 pp",
  titleMain: "Vivid Sydney",
  titleItalic: "Cruises.",
  body: "Sail right through the world's biggest festival of light. Vivid Bridge, the lit-up Opera House sails, the Campbell's Cove flame display, from the deck of the Magic.",
  ctaFeatured: "Get Vivid tickets",
  ctaCompact: "Get Tickets",
  compactSub: "3 cruise tiers · 90 min",
  featuredFooter: "3 cruise tiers · 90 min",
 },
 nye: {
  href: "/cruises/nye-cruises-sydney-harbour",
  testid: "ticketed-nye",
  ring: "ring-amber-300/30",
  shadowFeatured: "shadow-[0_40px_90px_-30px_rgba(245,158,11,0.45)]",
  shadowCompact: "shadow-[0_30px_70px_-25px_rgba(245,158,11,0.4)]",
  accentText: "text-amber-300",
  accentBadgeBg: "bg-amber-300",
  accentBadgeText: "text-[hsl(225,75%,12%)]",
  img: MAGIC_PHOTOS.nyeFireworksHero,
  alt: "Sydney NYE fireworks from the deck of MV Sydney Magic",
  bgGradient: "bg-gradient-to-t from-[hsl(225,75%,6%)] via-[hsla(225,70%,8%,0.75)] to-[hsla(225,60%,12%,0.25)]",
  bgOverlay1: "bg-[radial-gradient(ellipse_at_30%_85%,hsla(225,75%,6%,0.7)_0%,transparent_55%)]",
  bgOverlay2: "bg-gradient-to-br from-amber-500/25 via-transparent to-rose-500/15 mix-blend-overlay",
  compactBgGradient: "bg-gradient-to-r from-[hsl(225,70%,8%)]/95 via-[hsl(225,60%,10%)]/55 to-transparent",
  compactBgOverlay: "bg-gradient-to-tr from-amber-500/20 via-transparent to-rose-500/15 mix-blend-overlay",
  ribbon: "Selling fast · 2026",
  pricePill: "From $1,250 pp",
  dateLabel: "31 Dec 2026 · boarding 7:30pm",
  compactDate: "31 Dec 2026",
  compactBadgeText: "From $1,250 pp",
  titleMain: "NYE Fireworks",
  titleItalic: "Cruise.",
  body: "Sydney's most iconic night, from the best seat in the harbour. 9pm family fireworks, the Light Parade, then front-row for the midnight Bridge cascade. Canapés, buffet, full bar, DJ.",
  ctaFeatured: "Get NYE tickets",
  ctaCompact: "Get Tickets",
  compactSub: "9pm + midnight · light parade",
  featuredFooter: "~6 hours · 9pm + midnight fireworks",
 },
 "australia-day": {
  href: "/cruises/australia-day-cruises-sydney-harbour",
  testid: "ticketed-ausday",
  ring: "ring-cyan-400/30",
  shadowFeatured: "shadow-[0_40px_90px_-30px_rgba(6,182,212,0.45)]",
  shadowCompact: "shadow-[0_30px_70px_-25px_rgba(6,182,212,0.4)]",
  accentText: "text-cyan-300",
  accentBadgeBg: "bg-cyan-300",
  accentBadgeText: "text-[hsl(220,75%,12%)]",
  img: MAGIC_PHOTOS.australiaDayHarbour,
  alt: "Australia Day cruise · group on the bow waving Aussie flags · Sydney Harbour Bridge backdrop",
  bgGradient: "bg-gradient-to-t from-[hsl(220,75%,6%)] via-[hsla(220,70%,8%,0.75)] to-[hsla(200,60%,12%,0.25)]",
  bgOverlay1: "bg-[radial-gradient(ellipse_at_30%_85%,hsla(220,75%,6%,0.7)_0%,transparent_55%)]",
  bgOverlay2: "bg-gradient-to-br from-cyan-500/20 via-transparent to-emerald-400/15 mix-blend-overlay",
  compactBgGradient: "bg-gradient-to-r from-[hsl(220,70%,8%)]/95 via-[hsl(200,60%,12%)]/55 to-transparent",
  compactBgOverlay: "bg-gradient-to-tr from-cyan-500/20 via-transparent to-emerald-400/15 mix-blend-overlay",
  ribbon: "Public Holiday · 2027",
  pricePill: "Adult $145 · Child $70",
  dateLabel: "26 Jan 2027 · Day + Night sessions",
  compactDate: "26 Jan 2027",
  compactBadgeText: "Public Holiday",
  titleMain: "Australia Day",
  titleItalic: "Cruise.",
  body: "26th January 2027 on Sydney Harbour. Up close to the Regatta and Ferrython by day, the fireworks at night. Prawns, cold beers, sunshine, summer.",
  ctaFeatured: "Get Aus Day tickets",
  ctaCompact: "Get Tickets",
  compactSub: "Regatta · Ferrython · Fireworks",
  featuredFooter: "Two sessions · King St Wharf 6",
 },
 "boxing-day": {
  href: "/cruises/boxing-day-cruises-sydney",
  testid: "ticketed-boxing",
  ring: "ring-emerald-400/30",
  shadowFeatured: "shadow-[0_40px_90px_-30px_rgba(16,185,129,0.45)]",
  shadowCompact: "shadow-[0_30px_70px_-25px_rgba(16,185,129,0.4)]",
  accentText: "text-emerald-300",
  accentBadgeBg: "bg-emerald-300",
  accentBadgeText: "text-[hsl(170,75%,10%)]",
  img: "https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/mm8n8s47_boxing-day-cruises-sydney-harbour-magic.jpg",
  alt: "Boxing Day cruise on Sydney Harbour · Sydney to Hobart yacht race start from the deck of the Magic",
  bgGradient: "bg-gradient-to-t from-[hsl(170,75%,6%)] via-[hsla(170,65%,8%,0.75)] to-[hsla(180,55%,12%,0.25)]",
  bgOverlay1: "bg-[radial-gradient(ellipse_at_30%_85%,hsla(170,75%,6%,0.7)_0%,transparent_55%)]",
  bgOverlay2: "bg-gradient-to-br from-emerald-500/20 via-transparent to-teal-400/15 mix-blend-overlay",
  compactBgGradient: "bg-gradient-to-r from-[hsl(170,60%,8%)]/95 via-[hsl(180,55%,12%)]/55 to-transparent",
  compactBgOverlay: "bg-gradient-to-tr from-emerald-500/20 via-transparent to-teal-400/15 mix-blend-overlay",
  ribbon: "Sydney to Hobart · 2026",
  pricePill: "Adult $110 · Child $45",
  dateLabel: "26 Dec 2026 · 1pm yacht race start",
  compactDate: "26 Dec 2026",
  compactBadgeText: "Sydney to Hobart",
  titleMain: "Boxing Day",
  titleItalic: "Cruise.",
  body: "Front-row seat to the Sydney to Hobart yacht race start. Champagne brunch, festive seafood, beverage package, then watch the fleet thunder past on the way out the Heads.",
  ctaFeatured: "Get Boxing Day tickets",
  ctaCompact: "Get Tickets",
  compactSub: "Festive seafood · beverage pkg",
  featuredFooter: "11:45am to 3:15pm · King St Wharf 6",
 },
};

function FeaturedCard({ card }) {
 return (
  <Link to={card.href} className={`lg:col-span-7 group relative rounded-3xl overflow-hidden ring-1 ${card.ring} ${card.shadowFeatured} aspect-[5/6] lg:aspect-auto lg:min-h-[640px] block`} data-testid={card.testid}>
   <img src={card.img} alt={card.alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1500ms]" />
   <div className={`absolute inset-0 ${card.bgGradient}`} />
   <div className={`absolute inset-0 ${card.bgOverlay1}`} />
   <div className={`absolute inset-0 ${card.bgOverlay2}`} />

   {/* Top-left: featured ribbon */}
   <div className="absolute top-6 left-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/95 text-fuchsia-700 text-[10px] uppercase tracking-[0.28em] font-black shadow-lg">
    <Sparkles className="w-3 h-3" fill="currentColor" strokeWidth={0} /> {card.ribbon}
   </div>

   {/* Top-right: price pill */}
   <div className={`absolute top-6 right-6 inline-flex items-center gap-2 px-4 py-2 rounded-full ${card.accentBadgeBg} ${card.accentBadgeText} text-[10px] uppercase tracking-[0.22em] font-black shadow-lg`}>
    {card.pricePill}
   </div>

   {/* Bottom content */}
   <div className="absolute bottom-0 left-0 right-0 p-7 lg:p-12">
    <div className={`${card.accentText} text-[11px] uppercase tracking-[0.32em] font-bold mb-3 inline-flex items-center gap-2`}>
     <Calendar className="w-3.5 h-3.5" /> {card.dateLabel}
    </div>
    <h3 className="text-white font-black tracking-tight leading-[0.95] mb-4" style={{ fontSize: "clamp(2.25rem, 4.5vw, 4rem)", letterSpacing: "-0.025em" }}>
     {card.titleMain} <span className="italic font-light text-amber-300">{card.titleItalic}</span>
    </h3>
    <p className="text-white/80 text-base md:text-lg leading-[1.6] max-w-xl mb-7">
     {card.body}
    </p>
    <div className="flex flex-wrap items-center gap-3">
     <span className={`inline-flex items-center gap-2 px-5 py-3 rounded-full ${card.accentBadgeBg} ${card.accentBadgeText} font-black text-xs tracking-[0.2em] uppercase group-hover:opacity-90 transition-opacity`}>
      {card.ctaFeatured} <ArrowRight className="w-3.5 h-3.5" />
     </span>
     <span className={`${card.accentText} text-[10px] uppercase tracking-[0.28em] font-bold`}>{card.featuredFooter}</span>
    </div>
   </div>
  </Link>
 );
}

function CompactCard({ card }) {
 return (
  <Link to={card.href} className={`group relative rounded-3xl overflow-hidden ring-1 ${card.ring} ${card.shadowCompact} flex-1 min-h-[200px]`} data-testid={card.testid}>
   <img src={card.img} alt={card.alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1500ms]" />
   <div className={`absolute inset-0 ${card.compactBgGradient}`} />
   <div className={`absolute inset-0 ${card.compactBgOverlay}`} />

   <div className="relative h-full flex flex-col justify-between p-6 lg:p-7">
    <div className="flex items-start justify-between gap-3">
     <div className={`${card.accentText} text-[10px] uppercase tracking-[0.32em] font-bold inline-flex items-center gap-2`}>
      <Calendar className="w-3 h-3" /> {card.compactDate}
     </div>
     <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full ${card.accentBadgeBg} ${card.accentBadgeText} text-[9px] uppercase tracking-[0.22em] font-black`}>
      {card.compactBadgeText}
     </span>
    </div>
    <div>
     <div className="text-white/55 text-[10px] uppercase tracking-[0.28em] font-bold mb-1">{card.compactSub}</div>
     <h3 className="text-white font-black tracking-tight leading-[1.0] mb-2" style={{ fontSize: "clamp(1.5rem, 2.6vw, 2.25rem)", letterSpacing: "-0.02em" }}>
      {card.titleMain} <span className={`italic font-light ${card.accentText}`}>{card.titleItalic}</span>
     </h3>
     <div className="flex items-center justify-between gap-3">
      <span className="text-white/70 text-xs">{card.compactSub}</span>
      <span className={`${card.accentText} text-[10px] uppercase tracking-[0.28em] font-black inline-flex items-center gap-1 group-hover:gap-2 transition-all`}>
       {card.ctaCompact} <ArrowRight className="w-3 h-3" />
      </span>
     </div>
    </div>
   </div>
  </Link>
 );
}

export default function HomeTicketedCards() {
 const order = useHomeOrder();
 const validOrder = order.filter(id => CARDS[id]);
 if (validOrder.length < 4) return null;

 const [featuredId, ...compactIds] = validOrder;
 const featuredCard = CARDS[featuredId];

 return (
  <div className="grid lg:grid-cols-12 gap-5 lg:gap-6" data-testid="home-ticketed-grid">
   <FeaturedCard card={featuredCard} />
   <div className="lg:col-span-5 flex flex-col gap-5 lg:gap-6">
    {compactIds.map(id => <CompactCard key={id} card={CARDS[id]} />)}
   </div>
  </div>
 );
}
